import { useId, useMemo, useState, type ReactNode } from 'react';

import { useI18n } from '../i18n';
import { accentVar, cn } from '../lib/ui';

/**
 * Charts, drawn by hand in SVG.
 *
 * Rules these follow, deliberately:
 *   - Color identifies an entity, never a rank. Filtering out a member does
 *     not repaint the ones that remain.
 *   - Every chart has a table underneath it. Color is never the only way to
 *     read a value, and a screen reader gets real numbers.
 *   - Marks are thin, grid lines recede, and labels sit on the data instead of
 *     in a legend wherever there is room for them.
 *   - One y-scale. Ever.
 */

// --- Shared bits ------------------------------------------------------------

function ChartFrame({
  title,
  description,
  actions,
  children,
  table,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
  table?: ReactNode;
}) {
  const { t } = useI18n();
  const [showTable, setShowTable] = useState(false);

  return (
    <figure className="m-0">
      <figcaption className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-[15px] font-semibold tracking-tight text-[var(--ink)]">{title}</h3>
          {description ? (
            <p className="mt-0.5 text-[13px] text-[var(--ink-secondary)]">{description}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {actions}
          {table ? (
            <button
              type="button"
              onClick={() => setShowTable((current) => !current)}
              className="press rounded-full px-2.5 py-1 text-[12px] font-medium text-[var(--ink-secondary)] hover:bg-[var(--surface-sunken)]"
            >
              {showTable ? t('insights.chartView') : t('insights.tableView')}
            </button>
          ) : null}
        </div>
      </figcaption>

      {showTable && table ? table : children}
    </figure>
  );
}

function DataTable({
  columns,
  rows,
}: {
  columns: string[];
  rows: { key: string; color?: string; cells: ReactNode[] }[];
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="border-b border-[var(--line)] text-left text-[var(--ink-secondary)]">
            {columns.map((column, index) => (
              <th
                key={column}
                scope="col"
                className={cn('py-2 font-medium', index > 0 && 'text-right')}
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--line)]">
          {rows.map((row) => (
            <tr key={row.key}>
              {row.cells.map((cell, index) => (
                <td
                  key={index}
                  className={cn(
                    'py-2 text-[var(--ink)]',
                    index > 0 && 'numeric text-right tabular-nums',
                  )}
                >
                  {index === 0 && row.color ? (
                    <span className="flex items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ background: row.color }}
                        aria-hidden="true"
                      />
                      {cell}
                    </span>
                  ) : (
                    cell
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// --- Ranked bars ------------------------------------------------------------

export type RankedItem = {
  id: string;
  label: string;
  color: string;
  value: number;
  secondary?: number;
  icon?: ReactNode;
};

/**
 * A ranked bar list rather than a pie: with a dozen categories, a pie becomes a
 * color-matching puzzle, while bars stay sorted, directly labeled and
 * readable down to the smallest slice.
 */
export function RankedBars({
  title,
  description,
  items,
  formatValue,
  secondaryLabel,
  limit = 6,
}: {
  title: string;
  description?: string;
  items: RankedItem[];
  formatValue: (value: number) => string;
  secondaryLabel?: string;
  limit?: number;
}) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);

  const sorted = useMemo(() => [...items].sort((a, b) => b.value - a.value), [items]);
  const visible = expanded ? sorted : sorted.slice(0, limit);
  const max = Math.max(...sorted.map((item) => item.value), 1);

  if (sorted.length === 0) return null;

  return (
    <ChartFrame
      title={title}
      description={description}
      table={
        <DataTable
          columns={[title, t('insights.spent'), ...(secondaryLabel ? [secondaryLabel] : [])]}
          rows={sorted.map((item) => ({
            key: item.id,
            color: item.color,
            cells: [
              item.label,
              formatValue(item.value),
              ...(secondaryLabel ? [formatValue(item.secondary ?? 0)] : []),
            ],
          }))}
        />
      }
      actions={
        sorted.length > limit ? (
          <button
            type="button"
            onClick={() => setExpanded((current) => !current)}
            className="press rounded-full px-2.5 py-1 text-[12px] font-medium text-[var(--ink-secondary)] hover:bg-[var(--surface-sunken)]"
          >
            {expanded ? t('common.showLess') : t('common.showAll')}
          </button>
        ) : null
      }
    >
      <ul className="space-y-3.5">
        {visible.map((item) => (
          <li key={item.id}>
            <div className="mb-1.5 flex items-baseline justify-between gap-3">
              <span className="flex min-w-0 items-center gap-2 text-[14px] text-[var(--ink)]">
                {item.icon ? (
                  <span className="shrink-0" style={{ color: item.color }}>
                    {item.icon}
                  </span>
                ) : null}
                <span className="truncate">{item.label}</span>
              </span>
              <span className="numeric shrink-0 text-[14px] font-medium text-[var(--ink)]">
                {formatValue(item.value)}
              </span>
            </div>
            {/* Track and bar are separate elements so the bar can animate width
                without the track resizing under it. */}
            <div className="h-2 w-full overflow-hidden rounded-full bg-[var(--surface-sunken)]">
              <div
                className="h-full rounded-full transition-[width] duration-500 ease-[var(--ease-out-soft)]"
                style={{
                  width: `${Math.max((item.value / max) * 100, 1.5)}%`,
                  background: item.color,
                }}
              />
            </div>
          </li>
        ))}
      </ul>
    </ChartFrame>
  );
}

// --- Donut ------------------------------------------------------------------

/**
 * Reserved for the few-slices case (who a household's spending is for). Beyond
 * about five people it stops being readable and {@link RankedBars} is better.
 */
export function DonutChart({
  title,
  description,
  items,
  formatValue,
  centerLabel,
  centerValue,
}: {
  title: string;
  description?: string;
  items: RankedItem[];
  formatValue: (value: number) => string;
  centerLabel?: string;
  centerValue?: string;
}) {
  const { t } = useI18n();
  const gradientId = useId();
  const total = items.reduce((sum, item) => sum + item.value, 0);
  const sorted = useMemo(() => [...items].sort((a, b) => b.value - a.value), [items]);

  if (total <= 0) return null;

  const radius = 62;
  const circumference = 2 * Math.PI * radius;
  // A 2px gap of surface color between segments keeps neighbouring slices
  // legibly separate even when their hues are close.
  const gapDegrees = items.length > 1 ? 2 : 0;

  let cursor = -90;
  const segments = sorted.map((item) => {
    const share = item.value / total;
    const degrees = share * 360;
    const segment = {
      ...item,
      share,
      dash: (Math.max(degrees - gapDegrees, 0.5) / 360) * circumference,
      offset: (cursor / 360) * circumference,
      rotation: cursor,
    };
    cursor += degrees;
    return segment;
  });

  return (
    <ChartFrame
      title={title}
      description={description}
      table={
        <DataTable
          columns={[title, t('insights.spent'), '%']}
          rows={sorted.map((item) => ({
            key: item.id,
            color: item.color,
            cells: [
              item.label,
              formatValue(item.value),
              `${Math.round((item.value / total) * 100)}%`,
            ],
          }))}
        />
      }
    >
      <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-center sm:gap-8">
        <svg
          viewBox="0 0 160 160"
          className="h-[160px] w-[160px] shrink-0 -rotate-90"
          role="img"
          aria-label={t('a11y.chartDescription', { title })}
        >
          <defs>
            <clipPath id={gradientId}>
              <circle cx="80" cy="80" r="80" />
            </clipPath>
          </defs>
          {segments.map((segment) => (
            <circle
              key={segment.id}
              cx="80"
              cy="80"
              r={radius}
              fill="none"
              stroke={segment.color}
              strokeWidth={16}
              strokeDasharray={`${segment.dash} ${circumference - segment.dash}`}
              strokeDashoffset={-segment.offset}
              strokeLinecap="butt"
            >
              <title>{`${segment.label}: ${formatValue(segment.value)}`}</title>
            </circle>
          ))}
        </svg>

        <div className="min-w-0 flex-1">
          {centerValue ? (
            <div className="mb-4">
              <div className="text-[12px] uppercase tracking-wide text-[var(--ink-muted)]">
                {centerLabel}
              </div>
              <div className="numeric text-[24px] font-semibold text-[var(--ink)]">
                {centerValue}
              </div>
            </div>
          ) : null}
          {/* A legend is always present for two or more series; identity is
              never carried by color alone. */}
          <ul className="space-y-2">
            {sorted.map((item) => (
              <li key={item.id} className="flex items-center justify-between gap-3 text-[14px]">
                <span className="flex min-w-0 items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ background: item.color }}
                    aria-hidden="true"
                  />
                  <span className="truncate text-[var(--ink)]">{item.label}</span>
                </span>
                <span className="numeric shrink-0 text-[var(--ink-secondary)]">
                  {formatValue(item.value)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </ChartFrame>
  );
}

// --- Time series ------------------------------------------------------------

export type TimePoint = { key: string; label: string; value: number };

/**
 * A single-series area chart: the household's claimable balance, accumulating.
 * One series means no legend is needed — the title names it — and it means one
 * y-scale, which is the whole point.
 */
export function AreaChart({
  title,
  description,
  points,
  formatValue,
  accent = 'var(--accent)',
}: {
  title: string;
  description?: string;
  points: TimePoint[];
  formatValue: (value: number) => string;
  accent?: string;
}) {
  const { t, number } = useI18n();
  const gradientId = useId();
  const [hover, setHover] = useState<number | null>(null);

  const width = 640;
  const height = 200;
  const padding = { top: 16, right: 8, bottom: 26, left: 8 };

  const geometry = useMemo(() => {
    if (points.length === 0) return null;
    const max = Math.max(...points.map((p) => p.value), 1);
    const innerWidth = width - padding.left - padding.right;
    const innerHeight = height - padding.top - padding.bottom;
    const step = points.length > 1 ? innerWidth / (points.length - 1) : 0;

    const coords = points.map((point, index) => ({
      ...point,
      x: padding.left + index * step,
      y: padding.top + innerHeight - (point.value / max) * innerHeight,
    }));

    const line = coords
      .map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(1)},${c.y.toFixed(1)}`)
      .join(' ');
    const area = `${line} L${coords[coords.length - 1]!.x.toFixed(1)},${height - padding.bottom} L${coords[0]!.x.toFixed(1)},${height - padding.bottom} Z`;

    return { coords, line, area, max, innerHeight };
  }, [points, padding.left, padding.right, padding.top, padding.bottom]);

  if (!geometry || points.length < 2) return null;

  const active = hover === null ? null : geometry.coords[hover];

  return (
    <ChartFrame
      title={title}
      description={description}
      table={
        <DataTable
          columns={[t('insights.monthlyTitle'), title]}
          rows={points.map((point) => ({
            key: point.key,
            cells: [point.label, formatValue(point.value)],
          }))}
        />
      }
    >
      <div className="relative">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="h-[200px] w-full touch-none"
          role="img"
          aria-label={t('a11y.chartDescription', { title })}
          onPointerLeave={() => setHover(null)}
          onPointerMove={(event) => {
            const rect = event.currentTarget.getBoundingClientRect();
            const ratio = (event.clientX - rect.left) / rect.width;
            const index = Math.round(ratio * (points.length - 1));
            setHover(Math.min(Math.max(index, 0), points.length - 1));
          }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={accent} stopOpacity="0.22" />
              <stop offset="100%" stopColor={accent} stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Three recessive gridlines. Enough to judge magnitude, quiet enough
              to stay behind the data. */}
          {[0, 0.5, 1].map((fraction) => {
            const y = padding.top + geometry.innerHeight * fraction;
            return (
              <line
                key={fraction}
                x1={padding.left}
                x2={width - padding.right}
                y1={y}
                y2={y}
                stroke="var(--line)"
                strokeWidth="1"
              />
            );
          })}

          <path d={geometry.area} fill={`url(#${gradientId})`} />
          <path
            d={geometry.line}
            fill="none"
            stroke={accent}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {active ? (
            <g>
              <line
                x1={active.x}
                x2={active.x}
                y1={padding.top}
                y2={height - padding.bottom}
                stroke="var(--line-strong)"
                strokeWidth="1"
              />
              {/* A ring of surface color keeps the marker readable wherever it
                  lands on the fill. */}
              <circle cx={active.x} cy={active.y} r="6" fill="var(--surface)" />
              <circle cx={active.x} cy={active.y} r="4.5" fill={accent} />
            </g>
          ) : null}

          <text
            x={padding.left}
            y={height - 8}
            className="fill-[var(--ink-muted)] text-[11px]"
            style={{ fontSize: 11 }}
          >
            {points[0]!.label}
          </text>
          <text
            x={width - padding.right}
            y={height - 8}
            textAnchor="end"
            className="fill-[var(--ink-muted)] text-[11px]"
            style={{ fontSize: 11 }}
          >
            {points[points.length - 1]!.label}
          </text>
          <text
            x={padding.left}
            y={padding.top - 4}
            className="fill-[var(--ink-muted)]"
            style={{ fontSize: 11 }}
          >
            {number(geometry.max / 100, true)}
          </text>
        </svg>

        {active ? (
          <div
            className="material-strong pointer-events-none absolute top-0 -translate-x-1/2 rounded-xl px-3 py-2 shadow-[var(--shadow-pop)]"
            style={{ left: `${(active.x / width) * 100}%` }}
          >
            <div className="text-[11px] text-[var(--ink-secondary)]">{active.label}</div>
            <div className="numeric text-[14px] font-semibold text-[var(--ink)]">
              {formatValue(active.value)}
            </div>
          </div>
        ) : null}
      </div>
    </ChartFrame>
  );
}

// --- Stacked columns --------------------------------------------------------

/** Claimed vs still-open, month by month. Two series, so a legend is required. */
export function StackedColumns({
  title,
  description,
  points,
  formatValue,
}: {
  title: string;
  description?: string;
  points: { key: string; label: string; claimed: number; open: number }[];
  formatValue: (value: number) => string;
}) {
  const { t } = useI18n();
  const [hover, setHover] = useState<string | null>(null);

  const max = Math.max(...points.map((p) => p.claimed + p.open), 1);
  if (points.length === 0) return null;

  const series = [
    { key: 'open', label: t('insights.stillOpen'), color: accentVar('sky') },
    { key: 'claimed', label: t('insights.claimed'), color: accentVar('teal') },
  ] as const;

  return (
    <ChartFrame
      title={title}
      description={description}
      table={
        <DataTable
          columns={[t('insights.monthlyTitle'), t('insights.stillOpen'), t('insights.claimed')]}
          rows={points.map((point) => ({
            key: point.key,
            cells: [point.label, formatValue(point.open), formatValue(point.claimed)],
          }))}
        />
      }
    >
      <div className="flex items-end gap-[3px] overflow-x-auto pb-1" style={{ height: 168 }}>
        {points.map((point) => {
          const total = point.claimed + point.open;
          const isHovered = hover === point.key;
          return (
            <div
              key={point.key}
              className="group relative flex min-w-[14px] flex-1 flex-col justify-end"
              style={{ height: '100%' }}
              onPointerEnter={() => setHover(point.key)}
              onPointerLeave={() => setHover(null)}
            >
              {isHovered ? (
                <div className="material-strong absolute bottom-full left-1/2 z-10 mb-2 -translate-x-1/2 whitespace-nowrap rounded-xl px-3 py-2 text-left shadow-[var(--shadow-pop)]">
                  <div className="text-[11px] text-[var(--ink-secondary)]">{point.label}</div>
                  <div className="numeric text-[13px] font-semibold text-[var(--ink)]">
                    {formatValue(total)}
                  </div>
                </div>
              ) : null}

              {/* 2px of surface between the two stacked segments so the join is
                  visible without relying on the hue difference alone. */}
              <div
                className="w-full rounded-t-[4px] transition-opacity"
                style={{
                  height: `${(point.open / max) * 100}%`,
                  background: series[0].color,
                  opacity: hover && !isHovered ? 0.45 : 1,
                  marginBottom: point.open > 0 && point.claimed > 0 ? 2 : 0,
                }}
              />
              <div
                className="w-full transition-opacity"
                style={{
                  height: `${(point.claimed / max) * 100}%`,
                  background: series[1].color,
                  borderRadius: point.open > 0 ? 0 : '4px 4px 0 0',
                  opacity: hover && !isHovered ? 0.45 : 1,
                }}
              />
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex items-center justify-between">
        <span className="text-[11px] text-[var(--ink-muted)]">{points[0]!.label}</span>
        <ul className="flex items-center gap-4">
          {series.map((item) => (
            <li
              key={item.key}
              className="flex items-center gap-1.5 text-[12px] text-[var(--ink-secondary)]"
            >
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ background: item.color }}
                aria-hidden="true"
              />
              {item.label}
            </li>
          ))}
        </ul>
        <span className="text-[11px] text-[var(--ink-muted)]">
          {points[points.length - 1]!.label}
        </span>
      </div>
    </ChartFrame>
  );
}

// --- Hero figure ------------------------------------------------------------

/**
 * Not a chart: one number, as large as it deserves to be. This is the question
 * the whole app exists to answer, so nothing competes with it.
 */
export function HeroFigure({
  label,
  value,
  caption,
  footnote,
}: {
  label: string;
  value: string;
  caption?: string;
  footnote?: ReactNode;
}) {
  return (
    <div>
      <div className="text-[13px] font-medium text-[var(--ink-secondary)]">{label}</div>
      <div className="numeric mt-1 text-[clamp(2.5rem,11vw,3.75rem)] font-semibold leading-[1.05] text-[var(--ink)]">
        {value}
      </div>
      {caption ? (
        <div className="mt-1.5 text-[14px] text-[var(--ink-secondary)]">{caption}</div>
      ) : null}
      {footnote ? <div className="mt-4">{footnote}</div> : null}
    </div>
  );
}
